// Link preview cards (FigJam's link unfurls), built from ordinary layers so they render, export and
// edit like anything else: an image on top, then the title, description and the site with its icon.
import type { NodeData } from './types.ts';
import type { NodeSpec } from './spec.ts';

export type LinkPreview = NonNullable<NodeData['linkPreview']>;

const WIDTH = 360;

/** The spec of a link preview card (top-left at x, y). Clicking it opens the link in prototypes. */
export function linkPreviewSpec(meta: LinkPreview, at: { x?: number; y?: number } = {}): NodeSpec {
  let host = meta.url;
  try {
    host = new URL(meta.url).hostname.replace(/^www\./, '');
  } catch {
    /* keep the text */
  }
  const ratio = meta.imageWidth && meta.imageHeight ? meta.imageHeight / meta.imageWidth : 1 / 1.91;
  // wide images (social cards) go across the top; square ones (logos, avatars) sit beside the text
  const embed = embedUrl(meta.url);
  // players (videos, Figma files) always get the big picture, with a play button over it
  const compact = !!meta.image && ratio > 0.75 && !embed;
  const children: NodeSpec[] = [];
  if ((meta.image || embed) && !compact) {
    const h = Math.round(Math.min(WIDTH * 0.75, Math.max(120, WIDTH * (meta.image ? ratio : 9 / 16))));
    const thumb: NodeSpec = meta.image ? { type: 'image', name: 'Thumbnail', src: meta.image, width: 'fill', height: h, fit: 'fill' } : { type: 'rect', name: 'Thumbnail', width: 'fill', height: h, fill: '#1e1e1e' };
    if (!embed) children.push(thumb);
    else
      children.push({
        type: 'frame',
        name: 'Player',
        width: 'fill',
        height: h,
        fill: '#1e1e1e',
        children: [
          { ...thumb, x: 0, y: 0, width: WIDTH, height: h } as NodeSpec,
          { type: 'ellipse', name: 'Play', x: WIDTH / 2 - 28, y: h / 2 - 28, width: 56, height: 56, fill: '#000000b3' },
          { type: 'polygon', name: 'Play icon', x: WIDTH / 2 - 9, y: h / 2 - 11, width: 22, height: 22, rotation: 90, fill: '#ffffff' },
        ],
      });
  }
  const body: NodeSpec[] = [{ type: 'text', name: 'Title', text: meta.title || host, width: 'fill', fontSize: 16, fontWeight: 600, lineHeight: 22, color: '#1e1e1e', truncate: 2 }];
  if (meta.description) body.push({ type: 'text', name: 'Description', text: meta.description, width: 'fill', fontSize: 13, lineHeight: 18, color: '#5e5e5e', truncate: 3 });
  body.push({
    type: 'frame',
    name: 'Site',
    layout: 'row',
    gap: 6,
    crossAlign: 'center',
    width: 'fill',
    fill: null,
    children: [...(meta.favicon ? [{ type: 'image', name: 'Icon', src: meta.favicon, width: 16, height: 16, radius: 3 } as NodeSpec] : []), { type: 'text', name: 'Provider', text: meta.provider || host, width: 'fill', fontSize: 12, lineHeight: 16, color: '#8a8a8a', truncate: 1 }],
  });
  children.push({ type: 'frame', name: 'Details', layout: 'column', gap: 6, padding: compact ? [0, 16, 0, 0] : 16, width: 'fill', fill: null, children: body });
  if (compact) children.unshift({ type: 'image', name: 'Thumbnail', src: meta.image, width: 88, height: 88, radius: 8, fit: 'fill' });
  return {
    type: 'frame',
    name: meta.title ? `Link: ${meta.title.slice(0, 60)}` : `Link: ${host}`,
    ...(at.x !== undefined ? { x: at.x } : {}),
    ...(at.y !== undefined ? { y: at.y } : {}),
    width: WIDTH,
    layout: compact ? 'row' : 'column',
    ...(compact ? { padding: [16, 0, 16, 16], gap: 12, crossAlign: 'center' } : {}),
    fill: '#ffffff',
    stroke: '#e6e6e6',
    strokeWidth: 1,
    radius: 12,
    clip: true,
    shadow: { color: '#0000001a', y: 2, blur: 8 },
    props: { linkPreview: embed ? { ...meta, embed } : meta, reactions: [{ trigger: 'ON_CLICK', action: { type: 'OPEN_URL', url: meta.url } }] },
    children,
  } as NodeSpec;
}

/** A link on its own (what paste and agents turn into a preview card). */
export function isLink(text: string): boolean {
  const t = text.trim();
  return /^https?:\/\/\S+$/i.test(t) && t.length < 2048;
}

/**
 * The player URL for links FigJam plays inline (embeds): YouTube, Vimeo, Loom, Spotify and Figma
 * files and prototypes. null for links that are only cards.
 */
export function embedUrl(link: string): string | null {
  let u: URL;
  try {
    u = new URL(link.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.replace(/^(www|m)\./, '');
  const id = (s: string | null | undefined) => (s && /^[\w-]{4,64}$/.test(s) ? s : null);
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const v = id(u.searchParams.get('v')) ?? id(u.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]+)/)?.[1]);
    return v ? `https://www.youtube-nocookie.com/embed/${v}` : null;
  }
  if (host === 'youtu.be') {
    const v = id(u.pathname.slice(1));
    return v ? `https://www.youtube-nocookie.com/embed/${v}` : null;
  }
  if (host === 'vimeo.com') {
    const v = u.pathname.match(/^\/(\d{4,12})(?:\/([\da-f]+))?/);
    return v ? `https://player.vimeo.com/video/${v[1]}${v[2] ? `?h=${v[2]}` : ''}` : null;
  }
  if (host === 'loom.com') {
    const v = id(u.pathname.match(/^\/(?:share|embed)\/([\da-f]+)/)?.[1]);
    return v ? `https://www.loom.com/embed/${v}` : null;
  }
  if (host === 'open.spotify.com') {
    const v = u.pathname.match(/^\/(track|album|playlist|episode|show|artist)\/([\w]+)/);
    return v ? `https://open.spotify.com/embed/${v[1]}/${v[2]}` : null;
  }
  if (host === 'figma.com' && /^\/(file|design|proto|board|slides)\//.test(u.pathname)) return `https://www.figma.com/embed?embed_host=vellum&url=${encodeURIComponent(u.toString())}`;
  return null;
}

/** Hosts the players embedUrl points at. */
const EMBED_HOSTS = new Set(['www.youtube-nocookie.com', 'player.vimeo.com', 'www.loom.com', 'open.spotify.com', 'www.figma.com']);

/**
 * The embed URL to play, or null: documents are shared and anyone who can edit one can set any
 * value, so only https players from the providers embedUrl knows are played.
 */
export function safeEmbed(src: string | undefined | null): string | null {
  if (!src) return null;
  try {
    const u = new URL(src);
    return u.protocol === 'https:' && EMBED_HOSTS.has(u.hostname) ? u.toString() : null;
  } catch {
    return null;
  }
}

/** A link from a document that is fine to open in a new tab (web and mail links only). */
export function safeLink(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'mailto:' ? u.toString() : null;
  } catch {
    return null;
  }
}
